import useSWRInfinite from 'swr/infinite'
import { API_URL } from '@/utils/constants'
import { blockScopedNoStoreFetcher } from '@/utils/block-scoped-fetcher'
import { dedupeByKey } from '@/utils/merge-paginated-sales'
import { collectionObjectApiPath } from '@/utils/collection-object'
import { MARKETPLACE_LABELS, MARKETPLACE_COLORS } from './useSalesVolume'

export { MARKETPLACE_LABELS, MARKETPLACE_COLORS }

/// One sale row coming back from `POST /r/sales/by-inscriptions`.
/// Mirrors `api::InscriptionSale` in the ord fork.
export interface GlobalSale {
	block_height: number
	block_timestamp: number
	inscription_id: string
	sequence_number: number
	old_satpoint: string
	new_satpoint: string
	txid: string
	sighash: number
	marketplace: number
	signals: number
	price_sats: number
	seller_address?: string | null
	buyer_address?: string | null
	inscriptions_in_tx?: number
	/// Token amount per inscription, for fungible-style collections
	/// (tap-, brc20-, etc.). Server attaches this from
	/// `inscriptions.meta_amount` so the activity feed can compute
	/// per-unit price for non-OW sales the same way the OW escrow
	/// path does. `null` / missing for non-fungible collections.
	amount?: number | string | null
}

export interface GlobalSalesPage {
	sales: GlobalSale[]
	has_more: boolean
	matched_inscriptions: number
}

const PAGE_LIMIT = 100

/// Fetch global cross-marketplace sales activity for a collection by slug.
/// Backed by `GET /collection/{slug}/sales` on ow-api, which resolves
/// inscription IDs server-side and proxies to ord's
/// `POST /r/sales/by-inscriptions` — the slug-based GET is cacheable at the
/// Cloudflare edge per-(slug, cursor) so repeat visits to the same
/// collection's activity are CDN hits.
///
/// • For collections in the api's REMOTE_SLUGS list (bitmap, sat-names,
///   etc.) the endpoint returns empty — those don't have a local
///   enumeration. The UI just renders "No global sales recorded yet".
/// • Pagination uses `before_height` cursor — stable even as new blocks
///   come in.
export default function useCollectionGlobalSales(slug: string | undefined) {
	const ready = !!API_URL && !!slug
	const baseUrl = ready ? `${API_URL}/${collectionObjectApiPath(slug!)}/sales` : null

	const getKey = (pageIndex: number, previousPage: GlobalSalesPage | null) => {
		if (!baseUrl) return null
		// Stop paging once the API tells us there's no more.
		if (previousPage && !previousPage.has_more) return null
		const prevRows = previousPage?.sales ?? []
		// The cursor only advances by block_height. If a FULL page is entirely
		// within one block (> PAGE_LIMIT sales in a single block), before_height
		// cannot move past it: an exclusive `<` comparator would drop the block's
		// remaining rows, an inclusive `<=` would re-fetch the same page forever.
		// Stop paging rather than loop. (dedupeByKey on flatten below also guards
		// the inclusive-overlap case at ordinary boundaries.)
		if (
			prevRows.length >= PAGE_LIMIT &&
			prevRows[0].block_height === prevRows[prevRows.length - 1].block_height
		)
			return null
		const before =
			prevRows.length > 0 ? prevRows[prevRows.length - 1].block_height : undefined
		const url =
			before === undefined
				? `${baseUrl}?limit=${PAGE_LIMIT}`
				: `${baseUrl}?before_height=${before}&limit=${PAGE_LIMIT}`
		// SWR key is the resolved URL — implicitly stable + the same key all
		// users hit, so SWR's request dedupe + CF's edge cache compound.
		return [url, pageIndex] as const
	}

	const swr = useSWRInfinite<GlobalSalesPage>(
		getKey as any,
		async (key: any) => {
			const [url, pageIndex] = key as [string, number]
			if (pageIndex === 0) return blockScopedNoStoreFetcher(url)
			const r = await fetch(url)
			if (!r.ok) throw new Error(`HTTP ${r.status} fetching ${url}`)
			return r.json()
		},
		{
			revalidateFirstPage: true,
			revalidateOnFocus: true,
			revalidateOnReconnect: true,
			refreshInterval: 10000,
			dedupingInterval: 5000,
			refreshWhenHidden: false,
		},
	)

	const pages = swr.data
	// De-dupe across pages: a block straddling a page boundary (or an inclusive
	// server cursor) can return the same sale on two consecutive pages.
	const sales: GlobalSale[] = pages
		? dedupeByKey(
				pages.flatMap((p) => p.sales),
				(s) => `${s.txid}:${s.inscription_id}`,
		  )
		: []
	const last = pages && pages.length > 0 ? pages[pages.length - 1] : undefined
	const hasMore = last ? last.has_more : false
	// `swr.size` leads `swr.data`: when a new page is requested, size bumps
	// immediately but data keeps its prior length until the page resolves. The
	// canonical "loading more" check is therefore `data[size - 1] === undefined`
	// (NOT `data[data.length - 1]`, which is the last RESOLVED page and is never
	// undefined, so the spinner/guards would never fire).
	const isLoadingMore =
		swr.size > 0 && !!pages && typeof pages[swr.size - 1] === 'undefined'
	const isLoadingInitial = !pages && !swr.error && ready
	const matchedInscriptions = pages?.[0]?.matched_inscriptions ?? 0

	return {
		sales,
		hasMore,
		isLoadingInitial,
		isLoadingMore,
		error: swr.error,
		size: swr.size,
		setSize: swr.setSize,
		matchedInscriptions,
	}
}
